import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, CircularProgress, Typography, Toolbar, AppBar, Select, MenuItem, FormControl } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SaveIcon from '@mui/icons-material/Save';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EventsApi from '../../api/eventsApi/eventsApi';
import { ReportData } from '../home/components/reportComponent/reportTypes';
import HtmlReportTemplate from './HtmlReportTemplate';
import { generateDocx } from './docxExport';
import './ReportView.css';
import { blobToBase64 } from '../home/components/reportComponent/reportUtils';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface ChartPayload {
    eventsData: any;
    startDate: string;
    endDate: string;
    selectedEventTypes: string[];
}

const waitForNextPaint = (): Promise<void> =>
    new Promise(resolve => requestAnimationFrame(() => resolve()));

const waitForFontsReady = async (): Promise<void> => {
    const maybeDoc = document as Document & { fonts?: { ready: Promise<unknown> } };
    if (maybeDoc.fonts?.ready) {
        try {
            await maybeDoc.fonts.ready;
        } catch {
            // Ignore font loading failures and continue export.
        }
    }
};

const waitForImagesReady = async (root: HTMLElement): Promise<void> => {
    const images = Array.from(root.querySelectorAll<HTMLImageElement>('img'));
    const pending = images.filter(img => !img.complete || img.naturalHeight === 0);
    if (!pending.length) return;

    await Promise.all(
        pending.map(img => new Promise<void>((resolve) => {
            const done = () => {
                img.removeEventListener('load', done);
                img.removeEventListener('error', done);
                resolve();
            };
            img.addEventListener('load', done, { once: true });
            img.addEventListener('error', done, { once: true });
        }))
    );
};

const renderPageToCanvas = async (pageElement: HTMLElement): Promise<HTMLCanvasElement> => {
    const exportRoot = document.createElement('div');
    exportRoot.setAttribute('data-export-clone-root', 'true');
    exportRoot.style.position = 'fixed';
    exportRoot.style.left = '-100000px';
    exportRoot.style.top = '0';
    exportRoot.style.pointerEvents = 'none';
    exportRoot.style.opacity = '0';
    exportRoot.style.zIndex = '-1';

    const clone = pageElement.cloneNode(true) as HTMLElement;
    exportRoot.appendChild(clone);
    document.body.appendChild(exportRoot);

    try {
        await waitForFontsReady();
        await waitForImagesReady(clone);
        await waitForNextPaint();

        return await html2canvas(clone, {
            scale: 1.5,
            useCORS: true,
            logging: false,
            backgroundColor: '#ffffff',
            imageTimeout: 15000,
        });
    } finally {
        exportRoot.remove();
    }
};

const ReportView = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [reportData, setReportData] = useState<ReportData | null>(null);
    const [userData, setUserData] = useState<{ firstName: string; lastName: string } | null>(null);
    const [attachments, setAttachments] = useState<{ [key: string]: string }>({});
    const [loading, setLoading] = useState(true);
    const [chartPayload, setChartPayload] = useState<ChartPayload | null>(null);
    const [exportFormat, setExportFormat] = useState('PDF');
    const [isSaving, setIsSaving] = useState(false);
    const [isPaginationReady, setIsPaginationReady] = useState(false);
    const reportPagesRef = useRef<HTMLDivElement>(null);
    const chartDateRange = useMemo(() => {
        const startDate = chartPayload?.startDate ? new Date(chartPayload.startDate) : undefined;
        const endDate = chartPayload?.endDate ? new Date(chartPayload.endDate) : undefined;
        return { startDate, endDate };
    }, [chartPayload?.startDate, chartPayload?.endDate]);

    useEffect(() => {
        const fetchData = async () => {
            if (!id) return;
            try {
                setLoading(true);

                const [userInfo, fullReport] = await Promise.all([
                    EventsApi.getUser(),
                    EventsApi.refreshPinFullReport(id),
                ]);
                setUserData({ firstName: userInfo.firstName, lastName: userInfo.lastName });
                setReportData(fullReport);

                const newAttachments: { [key: string]: string } = {};
                const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];

                const allFiles = [
                    ...(fullReport.pinInfo.fileAttachments || []),
                    ...(fullReport.questionAnswers || []).flatMap((qa: any) => qa.answer.fileAttachments || []),
                ].filter(file => imageExtensions.some(ext => (file.fileName?.toLowerCase() || '').endsWith(ext)));

                await Promise.all(allFiles.map(async file => {
                    try {
                        const blob = await EventsApi.getFileAttachment(file.id);
                        newAttachments[file.id] = await blobToBase64(blob);
                    } catch { /* skip missing files */ }
                }));

                if (fullReport.pinInfo.reportLocationPng) {
                    try {
                        const blob = await EventsApi.getFileAttachment(fullReport.pinInfo.reportLocationPng);
                        newAttachments[fullReport.pinInfo.reportLocationPng] = await blobToBase64(blob);
                    } catch { /* skip */ }
                }

                setAttachments(newAttachments);

                // Read chart eventsData from localStorage (set by wikipediaModal)
                const eventsStr = localStorage.getItem(`report_eventsdata_${id}`);
                if (eventsStr) {
                    try {
                        setChartPayload(JSON.parse(eventsStr));
                    } catch { /* ignore parse errors */ }
                    localStorage.removeItem(`report_eventsdata_${id}`);
                }

            } catch (error) {
                console.error('Error fetching report data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);

    /** Upload a PDF snapshot of the rendered report to the pin's attachments */
    const saveReportToBackend = async (): Promise<boolean> => {
        if (!reportData?.pinInfo?.Id || !reportPagesRef.current || !isPaginationReady) return false;
        try {
            setIsSaving(true);

            const pages = Array.from(
                reportPagesRef.current.querySelectorAll<HTMLElement>('[data-export-page="true"]')
            );
            if (!pages.length) {
                return false;
            }

            const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
            const A4_W = 210, A4_H = 297;

            for (let i = 0; i < pages.length; i++) {
                let canvas: HTMLCanvasElement;
                try {
                    canvas = await renderPageToCanvas(pages[i]);
                } catch (captureError: any) {
                    throw new Error(`Sayfa ${i + 1}/${pages.length} render edilemedi: ${captureError?.message || captureError}`);
                }
                const imgData = canvas.toDataURL('image/jpeg', 0.85);
                if (i > 0) pdf.addPage();
                pdf.addImage(imgData, 'JPEG', 0, 0, A4_W, A4_H);
            }

            const pdfBlob = pdf.output('blob');
            const ts = new Date().toLocaleDateString('tr-TR').replace(/\./g, '-');
            const safeName = (reportData.pinInfo.Name || 'rapor').replace(/[\\/:\."<>|]+/g, '_');
            const fileName = `${ts}_${safeName}_rapor.pdf`;

            const formData = new FormData();
            formData.append('Id', reportData.pinInfo.Id);
            formData.append('Files', new File([pdfBlob], fileName, { type: 'application/pdf' }));

            await EventsApi.updatePin(reportData.pinInfo.Id, formData);

            // Signal the source tab (wikipediaModal) to refresh its pdfFiles list
            localStorage.setItem(`report_saved_${reportData.pinInfo.Id}`, Date.now().toString());
            return true;
        } catch (err: any) {
            alert(`HATA: ${err?.message || JSON.stringify(err)}`);
            console.error('[ReportView] Rapor backend kaydı başarısız:', err);
            return false;
        } finally {
            setIsSaving(false);
        }
    };

    const handleDownload = async () => {
        if (!reportData) return;
        if (exportFormat === 'PDF') {
            window.print();
        } else if (exportFormat === 'DOCX') {
            try {
                await generateDocx(reportData, attachments, userData);
            } catch (error) {
                console.error('Error generating DOCX:', error);
                alert('DOCX oluşturulurken bir hata oluştu.');
            }
        }
    };

    const handleSaveReport = async () => {
        if (!reportData) return;
        if (!isPaginationReady) {
            alert('Sayfa düzeni halen hazırlanıyor. Lütfen birkaç saniye sonra tekrar deneyin.');
            return;
        }
        const success = await saveReportToBackend();
        if (success) {
            alert('Rapor başarıyla kaydedildi! Tesis sayfasındaki Kaydedilen Raporlar listesinde görünecektir.');
        } else {
            alert('Rapor kaydedilemedi. Lütfen tekrar deneyin.');
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', flexDirection: 'column', gap: 2 }}>
                <CircularProgress />
                <Typography>Rapor verileri yükleniyor...</Typography>
            </Box>
        );
    }

    if (!reportData) {
        return (
            <Box sx={{ p: 4 }}>
                <Typography color="error">Rapor bulunamadı veya bir hata oluştu.</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#74808a' }}>
            {/* Toolbar – hidden during print */}
            <AppBar
                position="sticky"
                className="no-print"
                elevation={2}
                sx={{ backgroundColor: '#0a3d4d' }}
            >
                <Toolbar sx={{ justifyContent: 'space-between', minHeight: 56 }}>
                    <Button
                        startIcon={<ArrowBackIcon />}
                        onClick={() => {
                            // Report view opens in a new blank tab — no history to go back to.
                            // Close the tab; if that fails (e.g. not script-opened), go to /reports.
                            try {
                                window.close();
                                // window.close() is async — give it 300ms before falling back.
                                setTimeout(() => {
                                    if (!window.closed) navigate('/reports');
                                }, 300);
                            } catch {
                                navigate('/reports');
                            }
                        }}
                        sx={{ color: '#ffffff', '&:hover': { backgroundColor: 'rgba(255,255,255,0.12)' } }}
                    >
                        Geri
                    </Button>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {exportFormat === 'PDF' && (
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 13 }}>
                                {isPaginationReady
                                    ? 'PDF Kaydet seçeneğini kullanın'
                                    : 'Sayfa düzeni hazırlanıyor...'}
                            </Typography>
                        )}

                        <FormControl size="small">
                            <Select
                                value={exportFormat}
                                onChange={(e) => setExportFormat(e.target.value)}
                                sx={{
                                    bgcolor: 'rgba(255,255,255,0.15)',
                                    color: '#ffffff',
                                    minWidth: 110,
                                    borderRadius: 1,
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.4)' },
                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.8)' },
                                    '& .MuiSvgIcon-root': { color: '#ffffff' },
                                }}
                            >
                                <MenuItem value="PDF">PDF</MenuItem>
                                <MenuItem value="DOCX">DOCX</MenuItem>
                            </Select>
                        </FormControl>

                        <Button
                            variant="contained"
                            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                            onClick={handleSaveReport}
                            disabled={isSaving || !isPaginationReady}
                            sx={{
                                backgroundColor: '#4CAF50',
                                color: '#ffffff',
                                fontWeight: 700,
                                '&:hover': { backgroundColor: '#388E3C' },
                                '&.Mui-disabled': { backgroundColor: 'rgba(76,175,80,0.5)', color: '#ffffff' },
                            }}
                        >
                            {isSaving ? 'Kaydediliyor...' : 'Raporu Kaydet'}
                        </Button>

                        <Button
                            variant="contained"
                            startIcon={<DownloadIcon />}
                            onClick={handleDownload}
                            disabled={isSaving}
                            sx={{
                                backgroundColor: '#ffffff',
                                color: '#0a3d4d',
                                fontWeight: 700,
                                '&:hover': { backgroundColor: '#e0f0f4', color: '#0a3d4d' },
                                '&.Mui-disabled': { backgroundColor: 'rgba(255,255,255,0.6)', color: '#0a3d4d' },
                            }}
                        >
                            İndir
                        </Button>
                    </Box>
                </Toolbar>
            </AppBar>

            {/* Wrapper to allow html2canvas to find .report-page elements for PDF generation */}
            <div ref={reportPagesRef}>
                <HtmlReportTemplate
                    reportData={reportData}
                    attachments={attachments}
                    userData={userData}
                    onPaginationReady={setIsPaginationReady}
                    eventsData={chartPayload?.eventsData}
                    chartStartDate={chartDateRange.startDate}
                    chartEndDate={chartDateRange.endDate}
                    selectedEventTypes={chartPayload?.selectedEventTypes}
                />
            </div>
        </Box>
    );
};

export default ReportView;
